import * as Sentry from '@sentry/react';

// Flag-driven backend routing (2026-09-04).
//
// Every REST and GraphQL-HTTP request resolves its host HERE, at request
// time. A company is routed to the "Mongo backend" (the compat client that
// replaced Prisma) when three independent conditions hold at once:
//
//   1. the kill-switch is off  (REACT_APP_DISABLE_BACKEND_OVERRIDE !== 'true'),
//   2. both Mongo URLs are configured at build time
//      (REACT_APP_REST_API_MONGO / REACT_APP_GRAPHQL_URL_MONGO, https only,
//      parsed, with a hostname),
//   3. redux says `state.user.backendTarget === 'mongo'`. That value is set
//      once per session (login / first boot) from the `mongoBackend` flag plus a
//      probe against the Mongo backend (see utils/backendTarget.js) and is
//      reset to 'env' by CLEAR_DATA.
//
// Anything else — pre-boot, no store, store read failure, unknown target —
// falls back to the env URLs. Routing must never break a request.
//
// Scope: REST + GraphQL HTTP only. GraphQL WS subscriptions and Pusher stay
// on the env backend on purpose (both backends share the same MongoDB and
// the Mongo backend does not serve `$subscribe`). Login and POST /verifyToken
// also always hit the env backend: the target is decided AFTER verifyToken
// and BEFORE SET_USER, so no data request straddles the switch.
//
// The store is attached from redux/store.js instead of imported here to
// avoid the cycle store -> ducks -> axios -> backendOverride -> store.

const BACKEND_TARGET_MONGO = 'mongo';
// The Bearer token travels to these hosts on every request: https only, and
// a real URL (a bare "https://" is a build mistake, not a backend). No http
// exception for localhost either — a local process could read the token off
// the wire; local runs use an https tunnel or leave the switch off.
const isAllowedBackendUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && parsed.hostname !== '';
  } catch (e) {
    return false;
  }
};

let storeRef = null;

// Kill-switch for non-prod runs: with REACT_APP_DISABLE_BACKEND_OVERRIDE="true"
// every request keeps the env-configured backend regardless of the flag. Only
// the exact string 'true' activates it (fail-closed). The guard is applied
// inside BOTH getters below — where the URL is decided.
let killSwitchWarned = false;
export function isBackendOverrideDisabled() {
  if (process.env.REACT_APP_DISABLE_BACKEND_OVERRIDE !== 'true') return false;
  if (!killSwitchWarned) {
    killSwitchWarned = true;
    // Loud on purpose: if this ever fires in prod, the variable leaked into
    // the build and would silently turn off the flagged cohort.
    // eslint-disable-next-line no-console
    console.warn(
      '[backendOverride] routing disabled by REACT_APP_DISABLE_BACKEND_OVERRIDE',
    );
    // Actionable signal (nobody watches prod browser consoles): shows up in
    // Sentry as a warning event. Best-effort — if the first call happens
    // before Sentry.init, the SDK drops it silently, which is fine.
    Sentry.captureMessage('backendOverride.killswitch_active', {
      level: 'warning',
      tags: { component: 'backendOverride' },
    });
  }
  return true;
}

export function attachBackendOverrideStore(store) {
  try {
    storeRef = store && typeof store.getState === 'function' ? store : null;
  } catch (e) {
    storeRef = null;
  }
}

const asTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

export function getMongoBackendUrls() {
  return {
    rest: asTrimmedString(process.env.REACT_APP_REST_API_MONGO),
    graphql: asTrimmedString(process.env.REACT_APP_GRAPHQL_URL_MONGO),
  };
}

export function isMongoBackendConfigured() {
  const { rest, graphql } = getMongoBackendUrls();
  return isAllowedBackendUrl(rest) && isAllowedBackendUrl(graphql);
}

// Read at REQUEST time, never cached: the target flips on login/logout and
// every request must see the current value.
let storeReadWarned = false;
function currentBackendTarget() {
  if (!storeRef) return null;
  try {
    const state = storeRef.getState();
    const user = state && typeof state === 'object' ? state.user : null;
    return user && typeof user === 'object' ? user.backendTarget : null;
  } catch (e) {
    if (!storeReadWarned) {
      storeReadWarned = true;
      // eslint-disable-next-line no-console
      console.warn('[backendOverride] store read failed; using env backend', e);
      Sentry.captureMessage('backendOverride.store_read_failed', {
        level: 'warning',
        tags: { component: 'backendOverride' },
      });
    }
    return null;
  }
}

export function isMongoBackendActive() {
  if (isBackendOverrideDisabled()) return false;
  if (!isMongoBackendConfigured()) return false;
  return currentBackendTarget() === BACKEND_TARGET_MONGO;
}

export function getRestApiUrl() {
  return isMongoBackendActive()
    ? getMongoBackendUrls().rest
    : process.env.REACT_APP_REST_API;
}

export function getGraphqlUrl() {
  return isMongoBackendActive()
    ? getMongoBackendUrls().graphql
    : process.env.REACT_APP_GRAPHQL_URL;
}
